import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Category {
  readonly id: string;
  readonly name_fr: string;
  readonly name_wo: string;
  readonly name_en: string;
  readonly slug: string;
  readonly parent_id: string | null;
  readonly image_url: string | null;
  readonly is_active: boolean;
  readonly sort_order: number;
  readonly product_count: number;
  readonly created_at: string;
  readonly updated_at: string;
  readonly children: readonly Category[];
}

export interface CategoryFormData {
  readonly name_fr: string;
  readonly name_wo?: string;
  readonly name_en?: string;
  readonly slug: string;
  readonly parent_id?: string | null;
  readonly image_url?: string | null;
  readonly is_active: boolean;
  readonly sort_order?: number;
}

export interface ReorderItem {
  readonly id: string;
  readonly sort_order: number;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const categoriesApi = {
  /** Fetch the full nested category tree. */
  getTree: () =>
    adminFetch<{ categories: readonly Category[] }>("/api/admin/categories"),

  /** Create a new category. */
  create: (data: CategoryFormData) =>
    adminFetch<Category>("/api/admin/categories", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** Update an existing category. */
  update: (id: string, data: Partial<CategoryFormData>) =>
    adminFetch<Category>(`/api/admin/categories/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  /** Delete a category, optionally moving children to another parent. */
  delete: (id: string, moveChildrenTo?: string | null) =>
    adminFetch<{ success: boolean; message: string }>(
      `/api/admin/categories/${id}`,
      {
        method: "DELETE",
        body: JSON.stringify({
          move_children_to: moveChildrenTo ?? null,
        }),
      },
    ),

  /** Bulk reorder categories within the same level. */
  reorder: (items: readonly ReorderItem[]) =>
    adminFetch<{ success: boolean; message: string }>(
      "/api/admin/categories/reorder",
      {
        method: "POST",
        body: JSON.stringify({ items }),
      },
    ),
} as const;
